import React, { useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import { Icons, Box, Button, Card, Typography } from "@curves/ui-base";
import { TermContent } from "@icoach/documents/components/";
import { dialogTypeMap } from "@icoach/documents/membership/staticData";
import { MembershipBillingRecordDialog, MembershipRecordDialog } from "@icoach/documents/membership/dialog";
import { default as termsData } from "@icoach/documents/membership/suspension/terms";
import { FocusModeToggle } from "@icoach/components/";
import { isEqual, refIsRequiredError } from "@util/utils";
import useToggleDialog from "@util/hook/useToggleDialog";
import useShowPDF from "@util/hook/useShowPDF";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import useMembershipSuspensionForm from "@icoach/documents/membership/suspension/hook/useMembershipSuspensionForm";
import { MembershipSuspensionFormProvider } from "@icoach/documents/membership/suspension/MembershipSuspensionFormContext";
import MembershipSuspensionFormInfoSection from "@icoach/documents/membership/suspension/components/MembershipSuspensionFormInfoSection";
import MembershipSuspensionSettingSection from "@icoach/documents/membership/suspension/components/MembershipSuspensionSettingSection";
import MembershipSuspensionBillingSection from "@icoach/documents/membership/suspension/components/MembershipSuspensionBillingSection";
import MembershipSuspensionAttachmentSection from "@icoach/documents/membership/suspension/components/MembershipSuspensionAttachmentSection";
import MembershipSuspensionNoticeSection from "@icoach/documents/membership/suspension/components/MembershipSuspensionNoticeSection";

const WorkspaceBox = props => {
	const { onOpenDialog } = props;

	return (
		<Box className="workspace-box">
			<SpeedDial ariaLabel="SpeedDial openIcon example" icon={<SpeedDialIcon openIcon={<Icons.Edit />} />}>
				<SpeedDialAction icon={<Icons.UserCircle />} tooltipTitle={"會籍紀錄"} onClick={() => onOpenDialog(dialogTypeMap.membershipRecord)} />
				<SpeedDialAction icon={<Icons.Dollar htmlColor="#a45bc8" />} tooltipTitle={"收費明細"} onClick={() => onOpenDialog(dialogTypeMap.membershipBillingRecord)} />
			</SpeedDial>
		</Box>
	);
};

const MembershipSuspensionContent = props => {
	const { isReadOnly = false, onReturnHistory } = props;
	const history = useHistory();
	/** @type {{ referenceID?: string, contractID?: string, peopleType?: string }} */
	const { referenceID: referenceIDProps, contractID: contractIDProps, peopleType: peopleTypeProps } = useParams();
	const showPDF = useShowPDF();
	const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
	const formRef = useRef({});

	const formAdapter = {
		getFormData: () => {
			const { month, day } = formRef.current.suspendDateLength.getResult();

			return {
				plugIns: {
					note: formRef.current.note.getResult(),
				},
				applyDate: formRef.current.applyDate.getResult(),
				employeeID: formRef.current.employeeID.getResult(),
				isConfirm: formRef.current.isConfirm.getResult()[0].checked,
				checkNextMembershipDebit: formRef.current.checkNextMembershipDebit
					? formRef.current.checkNextMembershipDebit.getResult()[0].checked
					: false,
				leaveReason: formRef.current.leaveReason?.getResult() ?? "",
				suspendReasonText: formRef.current.suspendReasonText.getResult(),
				isKeepDebit: formRef.current.isKeepDebit.getResult()[0].checked,
				suspendDay: day || 0,
				suspendMonth: month || 0,
			};
		},
		getSignImg: () => formRef.current.signImg.getResult(),
		validateForm: () => {
			return !refIsRequiredError(
				formRef.current.applyDate,
				formRef.current.employeeID,
				formRef.current.note,
				formRef.current.suspendReasonText,
				formRef.current.suspendDateLength,
				formRef.current.suspendStartDate,
				formRef.current.suspendStartDate,
				formRef.current.isKeepDebit,
				formRef.current.isConfirm,
				formRef.current.checkNextMembershipDebit
			);
		},
	};

	// 功能：正式送出成功 → 顯示 PDF 並導向唯讀頁
	const handleSubmitSuccess = resp => {
		const { completedPath, deviceOrderCompletedPath } = resp;
		let pdfs = [completedPath];
		if (deviceOrderCompletedPath) pdfs.push(deviceOrderCompletedPath);
		const path = CLUB_DOCUMENTS_ROUTER.SUSPENSION.GET_READONLY_PATH(peopleTypeProps, referenceIDProps, resp.contractID);
		showPDF({
			open: true,
			pdfUrl: pdfs,
		});
		window.setTimeout(() => {
			history.push(path);
		}, 5);
	};

	const {
		options, isMounted, validationErrors, formData,
		handleSubmitCreate, handleSubmitTemporary, handleFileChange,
		handleChangeApplyDate, handleSuspendTypeChange, handleSuspendReasonChange,
		handleSuspendDataChange, handleSuspendMonthChange, handleSuspendDayChange, handleExtensionChange,
	} = useMembershipSuspensionForm({
		formAdapter,
		isReadOnly,
		contractIDProps,
		referenceIDProps,
		onSubmitSuccess: handleSubmitSuccess,
		onSaveDraftSuccess: onReturnHistory,
	});

	const focusModeRef = useRef(null);

	useInactivityTimerToggle();

	const {
		membershipID,
		contractID,
		memberID,
		isCompleteContract, // 是否為正式文件
		contractVersion,
		contractVersionText,
	} = formData || {};

	return (
		<MembershipSuspensionFormProvider
			value={{
				formData,
				formRef,
				options,
				validationErrors,
				isReadOnly,
				handleFileChange,
				handleChangeApplyDate,
				handleSuspendTypeChange,
				handleSuspendReasonChange,
				handleSuspendDataChange,
				handleSuspendMonthChange,
				handleSuspendDayChange,
				handleExtensionChange,
			}}
		>
			{isMounted && (
				<React.Fragment>
					<FocusModeToggle ref={focusModeRef} showToggleFocusModeButton={!isReadOnly}>
						<TermContent sourceData={termsData[contractVersion] || []} className="mb-4" title="會員權暫停條款" subheader="依教育部體育署健身中心定型化契約" />
						<Card>
							<Card.CardContent className="px-4 pt-2 pb-1">
								<Box className="mt-3 mb-5">
									<MembershipSuspensionFormInfoSection />
									<MembershipSuspensionSettingSection />
									<MembershipSuspensionBillingSection />
									<MembershipSuspensionAttachmentSection />
									<MembershipSuspensionNoticeSection />
								</Box>
								<Box className="btn-group text-center my-1">
									<Button variant="outlined" color="secondary" className="btn-minWidth" onClick={onReturnHistory}>
										返回
									</Button>
									{!isReadOnly && (
										<React.Fragment>
											{!isCompleteContract && (
												<Button variant="outlined" color="secondary" className="btn-minWidth" onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmitTemporary)}>
													暫存檔案
												</Button>
											)}
											<Button variant="contained" color="secondary" className="btn-minWidth" onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmitCreate)}>
												{isCompleteContract ? "確認修改" : "確認送出"}
											</Button>
										</React.Fragment>
									)}
								</Box>
								<Typography variant="body2" className="text-center mb-0 px-2 font-color-3 my-4">
									{contractVersionText}
								</Typography>
							</Card.CardContent>
						</Card>
					</FocusModeToggle>
					{Boolean(membershipID) && (
						<WorkspaceBox
							memberID={memberID}
							contractID={contractID}
							isCompleteContract={isCompleteContract}
							onOpenDialog={handleOpenDialog}
						/>
					)}
					<MembershipRecordDialog open={isEqual(openTarget, dialogTypeMap.membershipRecord)} memberID={memberID} membershipID={membershipID} onClose={handleCloseDialog} />
					<MembershipBillingRecordDialog open={isEqual(openTarget, dialogTypeMap.membershipBillingRecord)} memberID={memberID} membershipID={membershipID} onClose={handleCloseDialog} />
				</React.Fragment>
			)}
		</MembershipSuspensionFormProvider>
	);
};

export default MembershipSuspensionContent;
